/**
 * ClashShortcut.js — Iter. 411.h
 *
 * Compact one-tap shortcut from the Dashboard to `/clash` (Clash des
 * Meutes). Themed to the user's team color (rouge / bleu) so it feels
 * personal — a red soldier sees a red glow, a blue soldier sees a blue
 * glow. Auto-hides for staff roles (coach / admin / super_admin) and
 * for users not yet assigned to a team.
 *
 * Requested by Youri Octeau on 2026-02-08:
 *   "un petit icone pour se rendre dans la section clash des meutes"
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Swords, ChevronRight } from 'lucide-react';
import { useLanguage, useAuth } from '../context/AppContext';


export default function ClashShortcut() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';

  const staff = ['coach', 'admin', 'super_admin'].includes(user?.role);
  if (staff) return null;
  const team = user?.team_id;
  if (!team) return null;

  const isRed = team === 'rouge';
  const label = fr ? 'Clash des Meutes' : 'Pack Clash';
  const sub = fr
    ? (isRed ? 'Peloton Rouge · classement live' : 'Peloton Bleu · classement live')
    : (isRed ? 'Red platoon · live ranking' : 'Blue platoon · live ranking');

  const tone = isRed
    ? {
        bg: 'linear-gradient(90deg, rgba(239,68,68,0.18), rgba(220,38,38,0.10), rgba(248,113,113,0.14))',
        border: '1px solid rgba(239,68,68,0.55)',
        shadow: '0 0 18px rgba(239,68,68,0.35), inset 0 0 0 1px rgba(248,113,113,0.10)',
        iconBg: 'from-red-500/35 to-rose-600/30',
        iconGlow: '0 0 12px rgba(239,68,68,0.55)',
        iconColor: 'text-red-100',
        iconShadow: '0 0 6px rgba(239,68,68,0.85)',
        labelColor: 'text-red-200',
        labelGlow: '0 0 6px rgba(248,113,113,0.55)',
        chevron: 'text-red-300',
      }
    : {
        bg: 'linear-gradient(90deg, rgba(59,130,246,0.18), rgba(37,99,235,0.10), rgba(96,165,250,0.14))',
        border: '1px solid rgba(59,130,246,0.55)',
        shadow: '0 0 18px rgba(59,130,246,0.35), inset 0 0 0 1px rgba(96,165,250,0.10)',
        iconBg: 'from-sky-500/35 to-blue-600/30',
        iconGlow: '0 0 12px rgba(59,130,246,0.55)',
        iconColor: 'text-sky-100',
        iconShadow: '0 0 6px rgba(59,130,246,0.85)',
        labelColor: 'text-sky-200',
        labelGlow: '0 0 6px rgba(96,165,250,0.55)',
        chevron: 'text-sky-300',
      };

  return (
    <button
      type="button"
      onClick={() => navigate('/clash')}
      data-testid="dashboard-clash-shortcut"
      className="w-full mb-4 group rounded-xl px-4 py-3 flex items-center justify-between transition-transform hover:-translate-y-0.5 active:scale-[0.99]"
      style={{ background: tone.bg, border: tone.border, boxShadow: tone.shadow }}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-9 h-9 rounded-lg bg-gradient-to-br ${tone.iconBg} flex items-center justify-center`}
          style={{ boxShadow: tone.iconGlow }}
        >
          <Swords
            className={`w-4 h-4 ${tone.iconColor}`}
            style={{ filter: `drop-shadow(${tone.iconShadow})` }}
          />
        </div>
        <div className="text-left">
          <p
            className={`text-[10px] uppercase tracking-[0.28em] font-bold ${tone.labelColor}`}
            style={{ textShadow: tone.labelGlow }}
          >
            {label}
          </p>
          <p className="text-xs text-foreground/80">{sub}</p>
        </div>
      </div>
      <ChevronRight
        className={`w-4 h-4 ${tone.chevron} group-hover:translate-x-0.5 transition-transform`}
      />
    </button>
  );
}
